
/**
 * Environment detection utilities
 */

// Detect if we're in a prerendering environment
export const isPrerender = (): boolean => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return true;
  }
  
  // Check for common prerender indicators
  const userAgent = navigator.userAgent || '';
  return (
    userAgent.includes('prerender') ||
    userAgent.includes('puppeteer') ||
    userAgent.includes('headless') ||
    userAgent.toLowerCase().includes('bot') ||
    window.navigator.webdriver ||
    // @ts-ignore - Non-standard property
    !!window.__PRERENDER_INJECTED
  );
};

// Detect if we're on a mobile device with improved debugging
export const isMobileDevice = (): boolean => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }
  
  // More detailed debugging for device detection
  const userAgent = navigator.userAgent || '';
  
  // First check for common mobile indicators in the user agent
  const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
  const mobileDetected = mobileRegex.test(userAgent);
  
  // Additional checks that may help determine mobile devices
  const hasTouchScreen = 'maxTouchPoints' in navigator && navigator.maxTouchPoints > 0;
  const touchPoints = navigator.maxTouchPoints || 0;
  
  // Log comprehensive details about the device
  console.log(`isMobileDevice() detailed check:
    - UserAgent: ${userAgent.substring(0, 50)}...
    - Mobile UserAgent test: ${mobileDetected}
    - Touch points: ${touchPoints}
    - Has touch screen: ${hasTouchScreen}
    - Screen width: ${typeof window !== 'undefined' ? window.innerWidth : 'N/A'}
    - Platform: ${navigator.platform || 'unknown'}
    - Final result: ${mobileDetected || (hasTouchScreen && touchPoints > 1)}
  `);
  
  // Include touch screen capability in our mobile detection for tablets
  // but require at least 2 touch points to avoid false positives
  return mobileDetected || (hasTouchScreen && touchPoints > 1);
};

// Detect if we're on a low-end device 
export const isLowEndDevice = (): boolean => {
  if (typeof navigator === 'undefined') {
    return false;
  }
  
  // Use hardware concurrency as a proxy for device capability
  const cpuCores = navigator.hardwareConcurrency || 0;
  
  // Memory API is not available in all browsers
  let lowMemory = false;
  if ('deviceMemory' in navigator) {
    // @ts-ignore - Not all browsers support this
    lowMemory = navigator.deviceMemory < 4;
  }
  
  // Log comprehensive details about the device capabilities
  console.log(`isLowEndDevice() check:
    - CPU cores: ${cpuCores}
    - Device memory: ${('deviceMemory' in navigator) ? (navigator as any).deviceMemory + 'GB' : 'unknown'}
    - Is mobile: ${isMobileDevice()}
    - Final result: ${cpuCores <= 4 || lowMemory || isMobileDevice()}
  `);
  
  return cpuCores <= 4 || lowMemory || isMobileDevice();
};

// New helper to determine if we're on iOS specifically
export const isIOSDevice = (): boolean => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }
  
  const userAgent = navigator.userAgent || '';
  const isIOS = /iPhone|iPad|iPod/i.test(userAgent);
  const isMacOS = /Mac OS/i.test(userAgent);
  const hasTouchScreen = 'maxTouchPoints' in navigator && navigator.maxTouchPoints > 0;
  
  // iOS devices or Mac with touch (could be iPad)
  return isIOS || (isMacOS && hasTouchScreen);
};
